import clsx from "clsx";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "common/components/ui/Accordion";
import RadioButton from "common/components/ui/RadioButton";
import { Tag } from "common/components/ui/Tag";
import {
  USER_PERMISSION_LEVELS,
  USER_PERMISSIONS_CATEGORIES,
} from "common/constants";
import { typedObjectEntries } from "common/helpers/utils";
import useMemberPermissionsState from "common/hooks/useMemberPermissionsState";
import type { UserPermissions } from "common/types";

export default function PermissionsSelection({
  permissions,
  setPermissions,
}: {
  permissions: UserPermissions;
  setPermissions: (updatedPermissions: UserPermissions) => void;
}) {
  const { memberPermissionsState } = useMemberPermissionsState(permissions);

  //some permissions have empty permission options, these are used for labels only
  const visiblePermissions = typedObjectEntries(
    USER_PERMISSIONS_CATEGORIES,
  ).filter(([, { permissionOptions }]) => permissionOptions.length);

  return (
    <>
      <button
        type="button"
        className="mb-3 ml-2.5 mr-auto text-button-sm text-purple-600"
        onClick={() => {
          setPermissions(
            visiblePermissions.map(([key]) => `${key}_full`) as UserPermissions,
          );
        }}
      >
        Set all permissions
      </button>
      <Accordion
        type="multiple"
        className="flex max-h-80 flex-col gap-y-3 overflow-y-auto scrollbar-thin"
      >
        {visiblePermissions.map(
          ([key, { label, permissionOptions, icon: Icon }]) => {
            const selectedPermissionLevel =
              USER_PERMISSION_LEVELS[memberPermissionsState[key]];
            return (
              <AccordionItem
                value={key}
                className="rounded-2xl border border-black-200 last:border-b"
                key={key}
              >
                <AccordionTrigger className="px-3.5 py-3">
                  <Icon
                    size={20}
                    className={clsx(
                      "mr-2.5",
                      key === "campaigns" && "rotate-90",
                    )}
                  />
                  <span className="text-sm-bold">{label}</span>
                  <Tag
                    variant={selectedPermissionLevel.tagVariant}
                    className="ml-auto"
                  >
                    {selectedPermissionLevel.label}
                  </Tag>
                </AccordionTrigger>
                <AccordionContent className="flex flex-col gap-y-2 border-t border-t-black-200 p-3">
                  {permissionOptions.map(({ description, level }) => {
                    const isSelected = memberPermissionsState[key] === level;

                    return (
                      <div className="flex items-start" key={level}>
                        <RadioButton
                          variant="purple"
                          id={`${key}_${level}`}
                          size="sm"
                          checked={isSelected}
                          onChange={() => {
                            const updatedPermissions = permissions.filter(
                              (permission) => !permission.startsWith(key),
                            );
                            if (level !== "none") {
                              updatedPermissions.push(
                                `${key}_${USER_PERMISSION_LEVELS[level].permissionName}`,
                              );
                            }
                            setPermissions(updatedPermissions);
                          }}
                        />
                        <label
                          className="flex flex-col pl-1.5"
                          htmlFor={`${key}_${level}`}
                        >
                          <span className="text-left text-xs-bold">
                            {USER_PERMISSION_LEVELS[level].label}
                          </span>
                          <span className="text-left text-button-xxs text-black-500">
                            {description}
                          </span>
                        </label>
                      </div>
                    );
                  })}
                </AccordionContent>
              </AccordionItem>
            );
          },
        )}
      </Accordion>
    </>
  );
}
